import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatTimestamp, listMyInvoices, type Invoice } from '../lib/invoices';

export function DashboardPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyInvoices({ limit: 50 })
      .then((page) => { if (!cancelled) setInvoices(page.invoices); })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <header className="page-toolbar">
        <div>
          <h1>Dashboard</h1>
          <p style={lead}>
            Signed in as {user?.displayName || user?.email}
            {user && user.roles.length > 0 && (
              <span style={{ color: 'var(--sj-text-subtle)' }}> · roles: {user.roles.join(', ')}</span>
            )}
          </p>
        </div>
        <Link to="/invoices/new" style={cta}>+ New invoice</Link>
      </header>

      <section style={{ marginTop: 24 }}>
        <h2 style={sectionHeading}>My invoices</h2>
        {error && <p style={errStyle}>{error}</p>}
        {invoices === null && !error && <p style={muted}>Loading...</p>}
        {invoices && invoices.length === 0 && (
          <p style={muted}>You have not uploaded any invoices yet.</p>
        )}
        {invoices && invoices.length > 0 && (
          <ul className="invoice-list">
            {invoices.map((inv) => (
              <li key={inv.invoiceId}>
                <Link to={`/invoices/${inv.invoiceId}`} className="invoice-card">
                  <div className="invoice-card-row">
                    <span className="invoice-card-title">
                      {inv.category}
                      {inv.category === 'Other' && inv.otherReason && (
                        <span className="invoice-card-subtitle"> · {inv.otherReason}</span>
                      )}
                    </span>
                    <StatusPill status={inv.status} />
                  </div>
                  <div className="invoice-card-meta">{formatTimestamp(inv.createdAt)}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusPill({ status }: { status: Invoice['status'] }) {
  const approved = status === 'APPROVED';
  const bg = approved ? 'var(--sj-success-bg)' : 'var(--sj-warning-bg)';
  const color = approved ? 'var(--sj-success-fg)' : 'var(--sj-warning-fg)';
  const border = approved ? 'var(--sj-success-line)' : 'var(--sj-warning-line)';
  return (
    <span
      style={{
        background: bg,
        color,
        border: `1px solid ${border}`,
        padding: '2px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}
    >
      {status}
    </span>
  );
}

const lead: React.CSSProperties = {
  color: 'var(--sj-text-muted)',
  margin: 0
};

const muted: React.CSSProperties = {
  color: 'var(--sj-text-subtle)'
};

const sectionHeading: React.CSSProperties = {
  fontSize: '1.4rem',
  marginBottom: 12
};

const cta: React.CSSProperties = {
  background: 'var(--sj-crimson)',
  color: '#fff',
  textDecoration: 'none',
  padding: '10px 18px',
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase'
};

const errStyle: React.CSSProperties = {
  background: 'var(--sj-danger-bg)',
  border: '1px solid var(--sj-danger-line)',
  color: 'var(--sj-danger-fg)',
  padding: 12,
  borderRadius: 'var(--radius-xs)'
};
